// src/v2/pulse/DataTrustDrilldown.jsx
//
// Portfolio Command Data Trust: six source-backed pillars.  The server owns
// freshness windows and scoring; this component only formats the response.

import { useEffect, useState } from "react";
import { T } from "../../app/tokens.js";
import { getCommandIndex, getDataTrustOpenRun, getDataTrustSourceConfidence, putDataTrustSourceConfidence } from "../../data/commandIndexClient.js";
import { SourceConfidence } from "./SourceConfidence.jsx";
import { emitCommandIndexStale } from "../../services/cacheBus.js";

const PILLAR_ORDER = ["headcount", "leases", "seats", "badging", "open_roles", "projects"];
const PILLAR_META = {
  headcount: { label: "Headcount", source: "Workday" },
  leases: { label: "Leases", source: "Spacebase" },
  seats: { label: "Seats / capacity", source: "Internal capacity schedule" },
  badging: { label: "Badging / attendance", source: "Envoy or Alta/Verkada" },
  open_roles: { label: "Open roles", source: "Greenhouse" },
  projects: { label: "Projects", source: "Slack + docs" },
};
const LEGACY_CHECK_PILLARS = {
  hc_fresh: "headcount",
  capacity_set: "seats",
  lease_source_fresh: "leases",
  lease_complete: "leases",
  util_live: "badging",
};
const CHECK_META = {
  hc_fresh: { type: "Fresh", label: "Headcount up to date", unit: "offices" },
  headcount_fresh: { type: "Fresh", label: "Refreshed within window", unit: "offices" },
  headcount_complete: { type: "Complete", label: "Headcount present", unit: "offices" },
  capacity_set: { type: "Complete", label: "Seats set", unit: "offices" },
  capacity_complete: { type: "Complete", label: "Seats set", unit: "offices" },
  lease_source_fresh: { type: "Fresh", label: "Lease source imported within 365 days", unit: "leases" },
  lease_fresh: { type: "Fresh", label: "Imported within 365 days", unit: "leases" },
  lease_complete: { type: "Complete", label: "Required lease fields present", unit: "leases" },
  badging_fresh: { type: "Fresh", label: "Attendance within window", unit: "offices" },
  badging_complete: { type: "Complete", label: "Offices with attendance", unit: "offices" },
  util_live: { type: "Fresh", label: "Attendance within window", unit: "offices" },
  open_roles_fresh: { type: "Fresh", label: "Greenhouse pulled within window", unit: "open roles" },
  open_roles_complete: { type: "Complete", label: "Open roles mapped", unit: "open roles" },
  projects_fresh: { type: "Fresh", label: "Sources read within window", unit: "sources" },
  projects_verified: { type: "Verified", label: "Status human-approved", unit: "projects" },
};

const DEFAULT_WEIGHTS = {
  headcount: 25,
  leases: 25,
  seats: 15,
  badging: 20,
  open_roles: 5,
  projects: 10,
};

const BAND = {
  clear: { label: "Clear", color: "var(--v2-green)", bg: "var(--v2-green-tint)" },
  attention: { label: "Watch", color: "var(--v2-amber)", bg: "var(--v2-amber-tint)" },
  action: { label: "Needs action", color: "var(--v2-red)", bg: "var(--v2-red-tint)" },
  excluded: { label: "Excluded", color: "var(--v2-mid)", bg: "var(--v2-hair)" },
};

function pillarBand(pillar) {
  if (pillar.excluded || pillar.exclusionReason) return "excluded";
  if (["clear", "attention", "action", "excluded"].includes(pillar.status)) return pillar.status;
  if (pillar.score === null || pillar.score === undefined) return "action";
  if (pillar.score >= 80) return "clear";
  if (pillar.score >= 60) return "attention";
  return "action";
}

const PASS_COPY = {
  headcount: "Current and complete",
  leases: "Current and complete",
  seats: "Set for all offices",
  badging: "Reporting for all offices",
  open_roles: "Current and mapped",
  projects: "Current and verified",
};

const FAILURE_COPY = {
  headcount_fresh: "Headcount needs refresh",
  hc_fresh: "Headcount needs refresh",
  headcount_complete: "Headcount missing for some offices",
  lease_source_fresh: "Needs re-import",
  lease_fresh: "Needs re-import",
  capacity_set: "Seats missing for some offices",
  capacity_complete: "Seats missing for some offices",
  badging_fresh: "Attendance needs refresh",
  util_live: "Attendance needs refresh",
  badging_complete: "Attendance missing for some offices",
  open_roles_fresh: "Open roles need refresh",
  open_roles_complete: "Open roles need mapping",
  projects_fresh: "Project sources need refresh",
  projects_verified: "Project status needs approval",
};

function healthFor(pillar) {
  if (pillar.excluded || pillar.exclusionReason) return { label: "Not connected yet", mark: "·", kind: "excluded" };
  const checks = (pillar.checks ?? []).filter((check) => check.scored !== false && !check.exclusionReason);
  const observed = checks.filter((check) => Number(check.applicable) > 0 && check.passing != null && Number.isFinite(Number(check.passing)));
  if (!observed.length) return { label: "No source evidence yet", mark: "·", kind: "excluded" };
  const failed = observed.find((check) => Number(check.passing) < Number(check.applicable));
  if (failed) {
    const date = failed.lastUpdated && new Date(failed.lastUpdated);
    const updated = date && !Number.isNaN(date.getTime())
      ? ` (last updated ${date.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" })})`
      : "";
    return { label: `${FAILURE_COPY[failed.id] ?? "Check needs attention"}${updated}`, mark: "×", kind: "action" };
  }
  // Partial check coverage is not evidence that every check passed.
  if (checks.length !== observed.length) return { label: "Some checks have no evidence", mark: "×", kind: "action" };
  return { label: PASS_COPY[pillar.key] ?? "Current and complete", mark: "✓", kind: "clear" };
}

function checkMark(check) {
  if (check.exclusionReason || check.scored === false || !check.applicable || check.passing == null) {
    return { text: "–", color: "var(--v2-light)", label: "not applicable" };
  }
  if (check.passing >= check.applicable) return { text: "✓", color: "var(--v2-green)", label: "pass" };
  if (check.passing > 0) return { text: "!", color: "var(--v2-amber)", label: "attention" };
  return { text: "×", color: "var(--v2-red)", label: "fail" };
}

function CompactChecks({ pillar }) {
  if (pillar.excluded || pillar.exclusionReason) return <span style={{ color: "var(--v2-mid)" }}>· Not connected yet</span>;
  const checks = (pillar.checks ?? []).filter((check) => check.scored !== false && !check.exclusionReason);
  if (!checks.length) return <span style={{ color: "var(--v2-mid)" }}>· No source evidence yet</span>;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 10px" }}>
      {checks.map((check) => {
        const mark = checkMark(check);
        const meta = CHECK_META[check.id];
        const hasCount = Number(check.applicable) > 0 && check.passing != null && Number.isFinite(Number(check.passing));
        const label = hasCount && Number(check.passing) < Number(check.applicable) && check.id in FAILURE_COPY
          ? FAILURE_COPY[check.id] : (meta?.type ?? check.type ?? "Check");
        return (
          <span key={check.id} title={check.label ?? meta?.label ?? check.id}
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span aria-label={mark.label} style={{ color: mark.color, fontWeight: T.fwBold }}>{mark.text}</span>
            {label}
            {hasCount && <small style={{ color: "var(--v2-mid)" }}>{check.passing}/{check.applicable}</small>}
            {!hasCount && <small style={{ color: "var(--v2-mid)" }}>No evidence</small>}
          </span>
        );
      })}
      {checks.length === 1 && checks[0].lastUpdated && (
        <small style={{ color: "var(--v2-mid)" }}>{formatFreshness(checks[0])}</small>
      )}
      {(pillar.checks ?? []).some((check) => check.scored === false || check.exclusionReason) && (
        <small style={{ color: "var(--v2-mid)" }}>Some checks excluded</small>
      )}
    </div>
  );
}

function makeLegacyPillars(breakdown) {
  const byPillar = Object.fromEntries(PILLAR_ORDER.map((key) => [key, []]));
  for (const check of breakdown?.checks ?? []) {
    if (check.id === "lease_human_reviewed") continue;
    const key = LEGACY_CHECK_PILLARS[check.id];
    if (key) byPillar[key].push(check);
  }
  return PILLAR_ORDER.map((key) => {
    const checks = byPillar[key];
    const passing = checks.reduce((sum, check) => sum + (check.passing ?? 0), 0);
    const applicable = checks.reduce((sum, check) => (
      sum + (check.scored === false ? 0 : (check.applicable ?? 0))
    ), 0);
    return {
      key,
      ...(PILLAR_META[key]),
      checks: checks.map((check) => ({
        ...check,
        legacyStatus: check.rate >= 80 ? "Clear" : check.rate >= 60 ? "Watch" : "Needs action",
      })),
      passing,
      applicable,
      score: applicable ? Math.round((passing / applicable) * 100) : key === "projects" ? null : null,
      weight: DEFAULT_WEIGHTS[key],
      effectiveWeight: key === "projects" ? 0 : undefined,
      excluded: key === "projects",
      exclusionReason: key === "projects" ? "projects_ingestion_not_wired" : undefined,
    };
  });
}

function normalizePillars(breakdown) {
  if (Array.isArray(breakdown?.pillars)) {
    const byKey = new Map(breakdown.pillars.map((pillar) => [pillar.key, pillar]));
    return PILLAR_ORDER.map((key) => ({
      key,
      ...PILLAR_META[key],
      ...(byKey.get(key) ?? {
        checks: [],
        score: null,
        passing: 0,
        applicable: 0,
        weight: DEFAULT_WEIGHTS[key],
        excluded: key === "projects",
        exclusionReason: key === "projects" ? "projects_ingestion_not_wired" : undefined,
        effectiveWeight: key === "projects" ? 0 : undefined,
      }),
      checks: (byKey.get(key)?.checks ?? []).filter((check) =>
        check.id !== "lease_ai_abstracted" && check.id !== "lease_human_reviewed"),
    }));
  }
  return makeLegacyPillars(breakdown);
}

function formatCount(check) {
  const meta = CHECK_META[check.id] ?? {};
  const unit = check.unit ?? meta.unit;
  if (check.exclusionReason || check.scored === false) return "—";
  if (!check.applicable || check.passing == null) return "No evidence";
  return `${check.passing ?? 0} / ${check.applicable} ${unit ?? "items"}`;
}

function formatFreshness(check) {
  if (check.exclusionReason || check.scored === false) {
    return check.exclusionReason === "human_review_not_activated"
      ? "review not activated"
      : "not wired";
  }
  if (check.id === "lease_complete") return "checked per active lease";
  if (!check.lastUpdated) return "no source evidence";
  const date = new Date(check.lastUpdated);
  if (Number.isNaN(date.getTime())) return "";
  return `last updated ${date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`;
}

function CheckRow({ check }) {
  const meta = CHECK_META[check.id] ?? {};
  const mark = checkMark(check);
  return (
    <div
      data-testid={`datatrust-check-row-${check.id}`}
      style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, minHeight: 24, fontSize: T.fsSm5, minWidth: 0 }}
    >
      <span
        aria-label={mark.label}
        style={{
          display: "inline-flex",
          width: 16,
          height: 16,
          flex: "0 0 16px",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          background: mark.color,
          color: "white",
          fontSize: T.fsXxs5,
          fontWeight: T.fwBold,
        }}
      >
        {mark.text}
      </span>
      <span
        data-testid={`datatrust-check-type-${check.id}`}
        style={{
          width: 64,
          flex: "0 0 64px",
          padding: "2px 4px",
          borderRadius: 4,
          background: "var(--v2-tint)",
          color: "var(--v2-mid)",
          fontSize: T.fsXxs5,
          fontWeight: T.fwBold,
          letterSpacing: ".04em",
          textAlign: "center",
          textTransform: "uppercase",
        }}
      >
        {check.type ?? meta.type ?? "Check"}
      </span>
      <span style={{ color: "var(--v2-dark)", minWidth: 0 }}>
        {check.label ?? meta.label ?? check.id}
      </span>
      {check.id === "hc_fresh" && (
        <span style={{ color: "var(--v2-mid)", fontSize: T.fsXxs5 }}>
          Workday feed refreshed weekly.
        </span>
      )}
      {check.id === "lease_complete" && (
        <span style={{ color: "var(--v2-mid)", fontSize: T.fsXxs5 }}>
          Size, start/end dates, lessor, and current-year rent are required.
        </span>
      )}
      {check.exclusionReason || check.scored === false ? (
        <span
          data-testid={`datatrust-check-excluded-${check.id}`}
          style={{ color: "var(--v2-light)", marginLeft: "auto", whiteSpace: "nowrap" }}
        >
          —
        </span>
      ) : (
        <span
          data-testid={`datatrust-check-count-${check.id}`}
          style={{ color: "var(--v2-mid)", marginLeft: "auto", whiteSpace: "nowrap" }}
        >
          {formatCount(check)}
        </span>
      )}
      <span style={{ color: "var(--v2-light)", fontSize: T.fsXxs5, whiteSpace: "nowrap" }}>
        {formatFreshness(check)}
      </span>
      {check.stale > 0 && (
        <span data-testid="datatrust-lease-source-stale" style={{ color: "var(--v2-amber)" }}>
          {check.stale} stale
        </span>
      )}
      {check.pending > 0 && (
        <span data-testid="datatrust-lease-source-pending" style={{ color: "var(--v2-light)" }}>
          {check.pending} pending · get it into Spacebase
        </span>
      )}
      {check.legacyStatus && (
        <span style={{ position: "absolute", left: -10000 }} data-testid="datatrust-check-legacy-status">
          {check.legacyStatus}
        </span>
      )}
      {check.id === "lease_source_fresh" && check.applicable > check.passing && !check.exclusionReason ? (
        <span
          data-testid="datatrust-lease-source-attention"
          style={{ color: "var(--v2-mid)", whiteSpace: "nowrap" }}
        >
          {check.applicable - check.passing} needing attention
          {check.pending > 0 ? ` · ${check.pending} pending reconciliation` : ""}
        </span>
      ) : (
        <span
          data-testid={`datatrust-check-needs-${check.id}`}
          style={{ color: "var(--v2-mid)", whiteSpace: "nowrap" }}
        >
          {!check.exclusionReason && check.scored !== false && check.applicable > check.passing
            ? `${check.applicable - check.passing} needing attention`
            : ""}
        </span>
      )}
    </div>
  );
}

function WeightText({ pillar }) {
  const configured = Number(pillar.weight);
  const effective = pillar.effectiveWeight;
  if (!Number.isFinite(configured)) return "—";
  if (pillar.excluded) return `${configured}% nominal · 0% effective (excluded)`;
  return `${configured}% nominal · ${typeof effective === "number"
    ? `${Math.round(effective * 1000) / 10}%` : "—"} effective`;
}

function OpenRunChip({ run, onOpenRun }) {
  if (!run) return null;
  return (
    <button
      type="button"
      data-testid="datatrust-open-run-chip"
      onClick={() => onOpenRun?.(run.id)}
      style={{
        border: "1px solid var(--v2-amber)",
        borderRadius: 999,
        padding: "4px 12px",
        background: "transparent",
        color: "var(--v2-amber)",
        fontSize: T.fsSm5,
        fontWeight: T.fwSemibold,
        cursor: "pointer",
      }}
    >
      Remediation in progress →
    </button>
  );
}

export function DataTrustDrilldown({
  commandIndex: commandIndexProp = null,
  onNavigate,
  userRole = "viewer",
  onOpenLinkedRun,
}) {
  const [commandIndex, setCommandIndex] = useState(commandIndexProp);
  const [loading, setLoading] = useState(!commandIndexProp);
  const [openRun, setOpenRun] = useState(undefined);
  const [confidence, setConfidence] = useState({});
  const [confidenceError, setConfidenceError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState({});
  const isAdmin = userRole === "admin";

  useEffect(() => {
    if (commandIndexProp) {
      setCommandIndex(commandIndexProp);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    getCommandIndex()
      .then((result) => { if (!cancelled) { setCommandIndex(result ?? null); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [commandIndexProp]);

  useEffect(() => {
    let cancelled = false;
    if (!isAdmin) { setOpenRun(null); return undefined; }
    getDataTrustOpenRun()
      .then((run) => { if (!cancelled) setOpenRun(run ?? null); })
      .catch(() => { if (!cancelled) setOpenRun(null); });
    return () => { cancelled = true; };
  }, [isAdmin]);

  useEffect(() => {
    let cancelled = false;
    if (!isAdmin) { setConfidence({}); setConfidenceError(""); return undefined; }
    getDataTrustSourceConfidence()
      .then(({ pillars }) => {
        if (!cancelled) setConfidence(Object.fromEntries(pillars.map((item) => [item.pillarKey, item])));
      })
      .catch(() => { if (!cancelled) setConfidenceError("Source confidence could not be loaded."); });
    return () => { cancelled = true; };
  }, [isAdmin]);

  async function saveConfidence(key, value, rationale) {
    const saved = await putDataTrustSourceConfidence(key, value, rationale);
    setConfidence((previous) => ({ ...previous, [key]: saved }));
    setConfidenceError("");
    setRefreshing(true);
    try {
      const fresh = await getCommandIndex();
      if (!fresh?.pillars) throw new Error("Index unavailable");
      setCommandIndex(fresh);
      emitCommandIndexStale();
    } catch {
      setConfidenceError("Source confidence was saved, but the score and opportunities could not be refreshed. They may be out of date.");
    } finally {
      setRefreshing(false);
    }
  }

  const dtPillar = commandIndex?.pillars?.find((pillar) => pillar.key === "data_trust");
  const breakdown = dtPillar?.breakdown ?? null;
  const pillars = normalizePillars(breakdown);
  const needsAttention = pillars.filter((pillar) => pillarBand(pillar) !== "excluded"
    && (healthFor(pillar).kind !== "clear" || pillarBand(pillar) !== "clear")).length;
  const score = dtPillar?.score ?? null;
  const band = dtPillar?.band ?? null;
  const bandLabel = { strong: "Strong", watch: "Watch", risk: "At Risk" }[band] ?? band;

  return (
    <div data-testid="datatrust-drilldown">
      <div
        data-testid="datatrust-card"
        style={{
          background: "var(--v2-card)",
          border: "1px solid var(--v2-line)",
          borderRadius: 16,
          boxShadow: "var(--v2-shadow-card)",
          padding: "20px 22px",
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
          <span style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: "var(--v2-dark)" }}>
            Data Trust
          </span>
          <span data-testid="datatrust-header-score" style={{ fontSize: T.fsMd5, fontWeight: T.fwBold }}>
            {score !== null ? `${score} / 100` : "— / 100"}
          </span>
          {band && <span style={{ color: "var(--v2-mid)", fontSize: T.fsSm5 }}>{bandLabel}</span>}
        </div>
        <p style={{ margin: "10px 0 18px", color: "var(--v2-mid)", fontSize: T.fsSm, lineHeight: 1.55 }}>
          Whether the information behind the portfolio is current, complete, and verified. Sources still being connected do not count against the score.
        </p>

        {loading ? (
          <div data-testid="datatrust-loading" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <div key={item} style={{ height: 58, borderRadius: 8, background: "var(--v2-hair)" }} />
            ))}
          </div>
        ) : (
          <>
            {refreshing && <p role="status" style={{ color: "var(--v2-mid)" }}>Refreshing Data Trust score and opportunities...</p>}
             {isAdmin && confidenceError && <p role="alert" style={{ color: "var(--v2-red)" }}>{confidenceError}</p>}
            <div
              data-testid="datatrust-pillars-table"
                 style={{ border: "1px solid var(--v2-line)", borderRadius: 10, overflowX: "auto" }}
            >
              <div
                style={{
                  display: "grid",
                   minWidth: 830,
                   gridTemplateColumns: "minmax(150px, 1.2fr) minmax(200px, 2.2fr) minmax(60px, .5fr) minmax(190px, 1.4fr) minmax(100px, .8fr)",
                  gap: 12,
                  padding: "9px 14px",
                  background: "var(--v2-tint)",
                  color: "var(--v2-mid)",
                  fontSize: T.fsXxs5,
                  fontWeight: T.fwBold,
                  letterSpacing: ".05em",
                  textTransform: "uppercase",
                }}
              >
                  <span title="Source name and connected system">Source</span>
                  <span title="Observed checks and available counts or freshness">Checks</span>
                  <span title="Effective score after source confidence">Score</span>
                  <span title="Source confidence and public note">Source confidence</span>
                  <span title="Current source status">Status</span>
              </div>
              {pillars.map((pillar, index) => {
                const bandInfo = BAND[pillarBand(pillar)];
                 const health = healthFor(pillar);
                 const isOpen = isAdmin && !!expanded[pillar.key];
                return (
                  <div
                    key={pillar.key}
                     style={{ borderTop: index ? "1px solid var(--v2-hair)" : "none" }}
                  >
                   <div data-testid={`datatrust-pillar-row-${pillar.key}`}
                      style={{ display: "grid", minWidth: 830, gridTemplateColumns: "minmax(150px, 1.2fr) minmax(200px, 2.2fr) minmax(60px, .5fr) minmax(190px, 1.4fr) minmax(100px, .8fr)",
                       gap: 12, alignItems: "center", padding: "13px 14px" }}>
                    <div>
                       <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--v2-dark)", fontSize: T.fsSm5, fontWeight: T.fwBold }}>
                         {isAdmin && <button type="button" aria-label={`${isOpen ? "Collapse" : "Expand"} ${pillar.label} details`}
                           aria-expanded={isOpen} aria-controls={`datatrust-detail-${pillar.key}`}
                           onClick={() => setExpanded((previous) => ({ ...previous, [pillar.key]: !previous[pillar.key] }))}
                           style={{ background: "none", border: 0, cursor: "pointer", color: "var(--v2-mid)" }}>
                           {isOpen ? "▾" : "▸"}
                         </button>}
                        {pillar.label}
                      </div>
                      <div style={{ color: "var(--v2-mid)", fontSize: T.fsXxs5, marginTop: 3 }}>
                        {pillar.source}
                      </div>
                    </div>
                      <div data-testid={`datatrust-health-${pillar.key}`}
                        style={{ color: "var(--v2-dark)", fontSize: T.fsSm5 }}>
                        <CompactChecks pillar={pillar} />
                      </div>
                      <strong data-testid={`datatrust-effective-score-${pillar.key}`}
                        style={{ color: pillar.score == null ? "var(--v2-mid)" : bandInfo.color }}>
                        {pillar.score == null ? "—" : `${Math.round(pillar.score)}%`}
                      </strong>
                      <SourceConfidence pillar={pillar}
                        confidence={isAdmin && confidence[pillar.key] ? confidence[pillar.key] : { value: pillar.confidence }}
                        publicNote={pillar.publicNote ?? (isAdmin ? confidence[pillar.key]?.rationale?.split(/[.!?\n]/)[0]?.slice(0, 90) : "")}
                        canEdit={isAdmin} onSave={saveConfidence} />
                    <span
                      data-testid={`datatrust-pillar-status-${pillar.key}`}
                      style={{
                        display: "inline-flex",
                        justifyContent: "center",
                        padding: "4px 8px",
                        borderRadius: 6,
                        background: bandInfo.bg,
                        color: bandInfo.color,
                        fontSize: T.fsXxs5,
                        fontWeight: T.fwBold,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {bandInfo.label}
                    </span>
                   </div>
                   {isOpen && (
                     <div id={`datatrust-detail-${pillar.key}`} data-testid={`datatrust-detail-${pillar.key}`}
                       style={{ padding: "14px 20px", background: "var(--v2-tint)", display: "grid", gap: 12 }}>
                       <div className="datatrust-checks" style={{ display: "grid", gap: 5 }}>
                         <strong>Checks</strong>
                         {(pillar.checks ?? []).map((check) => <CheckRow key={check.id} check={check} />)}
                         {!(pillar.checks ?? []).length && <span>No applicable evidence yet.</span>}
                       </div>
                       <div data-testid={`datatrust-pillar-score-${pillar.key}`}>
                         Checks score: {pillar.objectiveScore == null ? "—" : `${Math.round(pillar.objectiveScore)}%`}
                         {pillar.objectiveScore != null && <>{" × "}{pillar.confidence ?? "Not set"}/10{" = "}</>}
                         {pillar.objectiveScore == null && " · "}
                         Effective: {pillar.score == null ? "—" : `${Math.round(pillar.effectiveScore ?? pillar.score)}%`}
                       </div>
                       <div data-testid={`datatrust-pillar-weight-${pillar.key}`}>Weight: {WeightText({ pillar })}</div>
                       <div>Source confidence:
                         {confidence[pillar.key]?.rationale && <p>{confidence[pillar.key].rationale}</p>}
                         {confidence[pillar.key]?.updatedAt && <time dateTime={confidence[pillar.key].updatedAt}>
                           Updated {new Date(confidence[pillar.key].updatedAt).toLocaleDateString()}
                         </time>}
                         <span style={{ color: "var(--v2-mid)", fontSize: T.fsXxs5 }}> Admin-only, audited</span></div>
                     </div>
                   )}
                   </div>
                );
              })}
            </div>
              <div
                 data-testid="datatrust-summary-line"
                style={{
                  marginTop: 16,
                  padding: "12px 14px",
                  background: "var(--v2-amber-tint)",
                  borderRadius: 10,
                  color: "var(--v2-dark)",
                  fontSize: T.fsSm,
                }}
              >
                 {needsAttention} source{needsAttention === 1 ? "" : "s"} need attention. Composite is the weighted average of connected sources.
              </div>

             {isAdmin && openRun && (
              <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--v2-line)" }}>
                <OpenRunChip run={openRun} onOpenRun={onOpenLinkedRun} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}